import { supabase } from '../lib/supabase';
import { formatDate } from '../utils/formatters';

/**
 * Schedule Service
 * Handles conference program/schedule operations with Supabase
 * Converts between snake_case (database) and camelCase (React)
 */

const mapRoomToClient = (room) => ({
  id: room?.id,
  conferenceId: room?.conference_id,
  name: room?.name,
  location: room?.location,
  capacity: room?.capacity,
  sortOrder: room?.sort_order,
  createdAt: room?.created_at,
  updatedAt: room?.updated_at,
});

const mapSessionToClient = (s) => ({
  id: s?.id,
  conferenceId: s?.conference_id,
  roomId: s?.room_id,
  trackId: s?.track_id,
  title: s?.title,
  description: s?.description,
  sessionType: s?.session_type,
  sessionDate: s?.session_date,
  startTime: s?.start_time,
  endTime: s?.end_time,
  chairName: s?.chair_name,
  isPublished: s?.is_published,
  sortOrder: s?.sort_order,
  createdAt: s?.created_at,
  updatedAt: s?.updated_at,
  room: s?.conference_rooms ? {
    id: s?.conference_rooms?.id,
    name: s?.conference_rooms?.name,
    location: s?.conference_rooms?.location,
  } : null,
  track: s?.conference_tracks ? {
    id: s?.conference_tracks?.id,
    name: s?.conference_tracks?.name,
  } : null,
  items: (s?.session_items || [])
    ?.sort((a, b) => (a?.presentation_order || 0) - (b?.presentation_order || 0))
    ?.map(item => ({
      id: item?.id,
      sessionId: item?.session_id,
      submissionId: item?.submission_id,
      presentationOrder: item?.presentation_order,
      startTime: item?.start_time,
      durationMinutes: item?.duration_minutes,
      customTitle: item?.custom_title,
      customSpeaker: item?.custom_speaker,
      customAffiliation: item?.custom_affiliation,
      itemType: item?.item_type,
      createdAt: item?.created_at,
      paper: item?.conference_submissions ? {
        id: item?.conference_submissions?.id,
        title: item?.conference_submissions?.title,
        paperNumber: item?.conference_submissions?.paper_number,
        abstract: item?.conference_submissions?.abstract,
        keywords: item?.conference_submissions?.keywords,
        trackId: item?.conference_submissions?.track_id,
        author: item?.conference_submissions?.user_profiles ? {
          firstName: item?.conference_submissions?.user_profiles?.first_name,
          lastName: item?.conference_submissions?.user_profiles?.last_name,
          institution: item?.conference_submissions?.user_profiles?.institution,
          email: item?.conference_submissions?.user_profiles?.email,
        } : null,
      } : null,
    })),
});

export const scheduleService = {

  // ============ ROOMS ============

  async getRooms(conferenceId) {
    try {
      const { data, error } = await supabase
        ?.from('conference_rooms')
        ?.select('*')
        ?.eq('conference_id', conferenceId)
        ?.order('sort_order', { ascending: true });
      if (error) throw error;
      return data?.map(mapRoomToClient) || [];
    } catch (error) {
      console.error('Get rooms error:', error);
      throw error;
    }
  },

  async createRoom(conferenceId, roomData) {
    try {
      const { data, error } = await supabase
        ?.from('conference_rooms')
        ?.insert({
          conference_id: conferenceId,
          name: roomData?.name,
          location: roomData?.location || null,
          capacity: roomData?.capacity || null,
          sort_order: roomData?.sortOrder || 0,
        })
        ?.select()
        ?.single();
      if (error) throw error;
      return mapRoomToClient(data);
    } catch (error) {
      console.error('Create room error:', error);
      throw error;
    }
  },

  async updateRoom(roomId, updates) {
    try {
      const updateObj = {};
      if (updates?.name !== undefined) updateObj.name = updates.name;
      if (updates?.location !== undefined) updateObj.location = updates.location;
      if (updates?.capacity !== undefined) updateObj.capacity = updates.capacity;
      if (updates?.sortOrder !== undefined) updateObj.sort_order = updates.sortOrder;

      const { data, error } = await supabase
        ?.from('conference_rooms')
        ?.update(updateObj)
        ?.eq('id', roomId)
        ?.select()
        ?.single();
      if (error) throw error;
      return mapRoomToClient(data);
    } catch (error) {
      console.error('Update room error:', error);
      throw error;
    }
  },

  async deleteRoom(roomId) {
    try {
      const { error } = await supabase
        ?.from('conference_rooms')
        ?.delete()
        ?.eq('id', roomId);
      if (error) throw error;
    } catch (error) {
      console.error('Delete room error:', error);
      throw error;
    }
  },

  // ============ SESSIONS ============

  async getSessions(conferenceId) {
    try {
      const { data, error } = await supabase
        ?.from('conference_sessions')
        ?.select(`
          *,
          conference_rooms (id, name, location),
          conference_tracks (id, name),
          session_items (
            id, session_id, submission_id, presentation_order, start_time, duration_minutes,
            custom_title, custom_speaker, custom_affiliation, item_type, created_at,
            conference_submissions (
              id, title, abstract, keywords, paper_number, track_id,
              user_profiles (first_name, last_name, institution, email)
            )
          )
        `)
        ?.eq('conference_id', conferenceId)
        ?.order('session_date', { ascending: true })
        ?.order('start_time', { ascending: true });
      if (error) throw error;
      return data?.map(mapSessionToClient) || [];
    } catch (error) {
      console.error('Get sessions error:', error);
      throw error;
    }
  },

  async createSession(conferenceId, sessionData) {
    try {
      const { data, error } = await supabase
        ?.from('conference_sessions')
        ?.insert({
          conference_id: conferenceId,
          room_id: sessionData?.roomId || null,
          track_id: sessionData?.trackId || null,
          title: sessionData?.title,
          description: sessionData?.description || null,
          session_type: sessionData?.sessionType || 'paper',
          session_date: sessionData?.sessionDate,
          start_time: sessionData?.startTime,
          end_time: sessionData?.endTime,
          chair_name: sessionData?.chairName || null,
          is_published: sessionData?.isPublished || false,
          sort_order: sessionData?.sortOrder || 0,
        })
        ?.select()
        ?.single();
      if (error) throw error;
      return { id: data?.id, ...sessionData };
    } catch (error) {
      console.error('Create session error:', error);
      throw error;
    }
  },

  async updateSession(sessionId, updates) {
    try {
      const updateObj = {};
      if (updates?.roomId !== undefined) updateObj.room_id = updates.roomId;
      if (updates?.trackId !== undefined) updateObj.track_id = updates.trackId;
      if (updates?.title !== undefined) updateObj.title = updates.title;
      if (updates?.description !== undefined) updateObj.description = updates.description;
      if (updates?.sessionType !== undefined) updateObj.session_type = updates.sessionType;
      if (updates?.sessionDate !== undefined) updateObj.session_date = updates.sessionDate;
      if (updates?.startTime !== undefined) updateObj.start_time = updates.startTime;
      if (updates?.endTime !== undefined) updateObj.end_time = updates.endTime;
      if (updates?.chairName !== undefined) updateObj.chair_name = updates.chairName;
      if (updates?.isPublished !== undefined) updateObj.is_published = updates.isPublished;
      if (updates?.sortOrder !== undefined) updateObj.sort_order = updates.sortOrder;

      const { error } = await supabase
        ?.from('conference_sessions')
        ?.update(updateObj)
        ?.eq('id', sessionId);
      if (error) throw error;
    } catch (error) {
      console.error('Update session error:', error);
      throw error;
    }
  },

  async deleteSession(sessionId) {
    try {
      const { error } = await supabase
        ?.from('conference_sessions')
        ?.delete()
        ?.eq('id', sessionId);
      if (error) throw error;
    } catch (error) {
      console.error('Delete session error:', error);
      throw error;
    }
  },

  async publishAllSessions(conferenceId) {
    try {
      const { error } = await supabase
        ?.from('conference_sessions')
        ?.update({ is_published: true })
        ?.eq('conference_id', conferenceId);
      if (error) throw error;
    } catch (error) {
      console.error('Publish all sessions error:', error);
      throw error;
    }
  },

  // ============ SESSION ITEMS ============

  async getUnassignedPapers(conferenceId) {
    try {
      const { data: allAccepted, error: acceptedError } = await supabase
        ?.from('conference_submissions')
        ?.select(`
          id, title, abstract, keywords, paper_number, track_id,
          user_profiles (first_name, last_name, institution),
          conference_tracks (name)
        `)
        ?.eq('conference_id', conferenceId)
        ?.eq('status', 'accepted')
        ?.order('paper_number', { ascending: true });
      if (acceptedError) throw acceptedError;

      // Get already assigned submission IDs
      const { data: assignedItems, error: assignedError } = await supabase
        ?.from('session_items')
        ?.select('submission_id, conference_sessions!inner(conference_id)')
        ?.not('submission_id', 'is', null)
        ?.eq('conference_sessions.conference_id', conferenceId);
      if (assignedError) throw assignedError;

      const assignedIds = new Set(assignedItems?.map(a => a?.submission_id));

      return allAccepted
        ?.filter(s => !assignedIds?.has(s?.id))
        ?.map(sub => ({
          id: sub?.id,
          title: sub?.title,
          abstract: sub?.abstract,
          keywords: sub?.keywords,
          paperNumber: sub?.paper_number,
          trackId: sub?.track_id,
          trackName: sub?.conference_tracks?.name,
          author: {
            firstName: sub?.user_profiles?.first_name,
            lastName: sub?.user_profiles?.last_name,
            institution: sub?.user_profiles?.institution,
          },
        })) || [];
    } catch (error) {
      console.error('Get unassigned papers error:', error);
      throw error;
    }
  },

  async assignPaper(sessionId, submissionId, options = {}) {
    try {
      const { data, error } = await supabase
        ?.from('session_items')
        ?.insert({
          session_id: sessionId,
          submission_id: submissionId,
          presentation_order: options?.presentationOrder || 0,
          duration_minutes: options?.durationMinutes || 20,
          item_type: 'paper',
        })
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Assign paper error:', error);
      throw error;
    }
  },

  async addCustomItem(sessionId, itemData) {
    try {
      const { data, error } = await supabase
        ?.from('session_items')
        ?.insert({
          session_id: sessionId,
          submission_id: null,
          presentation_order: itemData?.presentationOrder || 0,
          duration_minutes: itemData?.durationMinutes || 30,
          custom_title: itemData?.customTitle,
          custom_speaker: itemData?.customSpeaker || null,
          custom_affiliation: itemData?.customAffiliation || null,
          item_type: 'custom',
        })
        ?.select()
        ?.single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Add custom item error:', error);
      throw error;
    }
  },

  async removeItem(itemId) {
    try {
      const { error } = await supabase
        ?.from('session_items')
        ?.delete()
        ?.eq('id', itemId);
      if (error) throw error;
    } catch (error) {
      console.error('Remove item error:', error);
      throw error;
    }
  },

  async reorderItems(sessionId, orderedItemIds) {
    try {
      const updates = orderedItemIds?.map((id, index) =>
        supabase
          ?.from('session_items')
          ?.update({ presentation_order: index })
          ?.eq('id', id)
          ?.eq('session_id', sessionId)
      );
      await Promise.all(updates);
    } catch (error) {
      console.error('Reorder items error:', error);
      throw error;
    }
  },

  async moveItem(itemId, newSessionId, newOrder) {
    try {
      const { error } = await supabase
        ?.from('session_items')
        ?.update({
          session_id: newSessionId,
          presentation_order: newOrder,
        })
        ?.eq('id', itemId);
      if (error) throw error;
    } catch (error) {
      console.error('Move item error:', error);
      throw error;
    }
  },

  // ============ AUTO-SCHEDULE ============

  async autoSchedule(conferenceId, rooms, tracks, conferenceDays, config = {}) {
    try {
      const {
        papersPerSession = 4,
        paperDuration = 20,
        breakDuration = 30,
        lunchDuration = 60,
        dayStartTime = '09:00',
        dayEndTime = '17:00',
      } = config;

      // Get all accepted papers grouped by track
      const { data: papers, error: papersError } = await supabase
        ?.from('conference_submissions')
        ?.select(`
          id, title, paper_number, track_id,
          user_profiles (first_name, last_name)
        `)
        ?.eq('conference_id', conferenceId)
        ?.eq('status', 'accepted')
        ?.order('paper_number', { ascending: true });
      if (papersError) throw papersError;

      if (!papers?.length) throw new Error('No accepted papers to schedule');

      // Delete existing sessions for a clean slate
      const { error: deleteError } = await supabase
        ?.from('conference_sessions')
        ?.delete()
        ?.eq('conference_id', conferenceId);
      if (deleteError) throw deleteError;

      // Group papers by track
      const papersByTrack = {};
      papers?.forEach(p => {
        const tid = p?.track_id || 'unassigned';
        if (!papersByTrack[tid]) papersByTrack[tid] = [];
        papersByTrack[tid].push(p);
      });

      const trackIds = Object.keys(papersByTrack);
      const sessionDurationMinutes = papersPerSession * paperDuration;

      // Helper to parse time strings
      const parseTime = (timeStr) => {
        const [h, m] = timeStr?.split(':')?.map(Number);
        return h * 60 + m;
      };
      const formatTime = (minutes) => {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      };

      const dayStart = parseTime(dayStartTime);
      const dayEnd = parseTime(dayEndTime);

      // Build time slots for a day (with breaks)
      const buildDaySlots = () => {
        const slots = [];
        let current = dayStart;

        // Morning session 1
        if (current + sessionDurationMinutes <= dayEnd) {
          slots.push({ start: current, end: current + sessionDurationMinutes, type: 'paper' });
          current += sessionDurationMinutes;
        }

        // Morning break
        if (current + breakDuration <= dayEnd) {
          slots.push({ start: current, end: current + breakDuration, type: 'break', title: 'Coffee Break' });
          current += breakDuration;
        }

        // Morning session 2
        if (current + sessionDurationMinutes <= dayEnd) {
          slots.push({ start: current, end: current + sessionDurationMinutes, type: 'paper' });
          current += sessionDurationMinutes;
        }

        // Lunch
        if (current + lunchDuration <= dayEnd) {
          slots.push({ start: current, end: current + lunchDuration, type: 'break', title: 'Lunch Break' });
          current += lunchDuration;
        }

        // Afternoon session 1
        if (current + sessionDurationMinutes <= dayEnd) {
          slots.push({ start: current, end: current + sessionDurationMinutes, type: 'paper' });
          current += sessionDurationMinutes;
        }

        // Afternoon break
        if (current + breakDuration <= dayEnd) {
          slots.push({ start: current, end: current + breakDuration, type: 'break', title: 'Afternoon Break' });
          current += breakDuration;
        }

        // Afternoon session 2
        if (current + sessionDurationMinutes <= dayEnd) {
          slots.push({ start: current, end: current + sessionDurationMinutes, type: 'paper' });
          current += sessionDurationMinutes;
        }

        return slots;
      };

      const createdSessions = [];

      // For each day, create sessions across rooms
      let paperQueue = {};
      trackIds.forEach(tid => {
        paperQueue[tid] = [...(papersByTrack[tid] || [])];
      });

      for (const day of conferenceDays) {
        const daySlots = buildDaySlots();

        for (const slot of daySlots) {
          if (slot.type === 'break') {
            // Create a break session (no room needed)
            const { data: breakSession, error: breakError } = await supabase
              ?.from('conference_sessions')
              ?.insert({
                conference_id: conferenceId,
                title: slot.title,
                session_type: 'break',
                session_date: day.date,
                start_time: formatTime(slot.start),
                end_time: formatTime(slot.end),
                is_published: false,
              })
              ?.select()
              ?.single();
            if (!breakError) createdSessions.push(breakSession);
            continue;
          }

          // Paper session: create one per room for each track that has remaining papers
          for (let roomIdx = 0; roomIdx < rooms?.length; roomIdx++) {
            // Find a track that still has papers
            const trackWithPapers = trackIds.find(tid => paperQueue[tid]?.length > 0);
            if (!trackWithPapers) break;

            const trackPapers = paperQueue[trackWithPapers]?.splice(0, papersPerSession);
            if (!trackPapers?.length) continue;

            const trackName = tracks?.find(t => t?.id === trackWithPapers)?.name || 'General';
            const room = rooms[roomIdx];

            // Create the session
            const { data: session, error: sessionError } = await supabase
              ?.from('conference_sessions')
              ?.insert({
                conference_id: conferenceId,
                room_id: room?.id,
                track_id: trackWithPapers === 'unassigned' ? null : trackWithPapers,
                title: `${trackName} Session`,
                session_type: 'paper',
                session_date: day.date,
                start_time: formatTime(slot.start),
                end_time: formatTime(slot.end),
                is_published: false,
              })
              ?.select()
              ?.single();
            if (sessionError) {
              console.error('Create session error:', sessionError);
              continue;
            }

            // Assign papers to the session
            for (let i = 0; i < trackPapers.length; i++) {
              await supabase?.from('session_items')?.insert({
                session_id: session?.id,
                submission_id: trackPapers[i]?.id,
                presentation_order: i,
                duration_minutes: paperDuration,
                item_type: 'paper',
              });
            }

            createdSessions.push(session);
          }
        }
      }

      return createdSessions;
    } catch (error) {
      console.error('Auto-schedule error:', error);
      throw error;
    }
  },

  // ============ PUBLIC SCHEDULE ============

  async getPublishedSchedule(conferenceId) {
    try {
      const { data, error } = await supabase
        ?.from('conference_sessions')
        ?.select(`
          *,
          conference_rooms (id, name, location),
          conference_tracks (id, name),
          session_items (
            id, session_id, submission_id, presentation_order, start_time, duration_minutes,
            custom_title, custom_speaker, custom_affiliation, item_type, created_at,
            conference_submissions (
              id, title, abstract, keywords, paper_number, track_id,
              user_profiles (first_name, last_name, institution, email)
            )
          )
        `)
        ?.eq('conference_id', conferenceId)
        ?.eq('is_published', true)
        ?.order('session_date', { ascending: true })
        ?.order('start_time', { ascending: true });
      if (error) throw error;
      return data?.map(mapSessionToClient) || [];
    } catch (error) {
      console.error('Get published schedule error:', error);
      return [];
    }
  },

  // ============ HELPERS ============

  getConferenceDays(startDate, endDate) {
    if (!startDate || !endDate) return [];
    const days = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const current = new Date(start);
    while (current <= end) {
      days.push({
        date: current.toISOString().split('T')[0],
        label: formatDate(current, { weekday: 'short', month: 'short', day: 'numeric', year: undefined }),
        dayNumber: days.length + 1,
      });
      current.setDate(current.getDate() + 1);
    }
    return days;
  },
};

export default scheduleService;
